import { QueryClient } from '@tanstack/react-query'
import { INotificationContentHandler } from '@/lib/handler/notification-content-handler'
import { NotificationType } from '@/types/notification'

/**
 * Products Picture Import Content Handler
 * Updates cache when a product import job is completed
 */
export default class ProductsPictureImportContentHandler implements INotificationContentHandler {
  async handle(notification: NotificationType, queryClient: QueryClient): Promise<void> {
    const { payload } = notification

    if (!payload || !payload.id) {
      return
    }

    const importId = payload.id
    const status = payload.status
    const successfulFiles = payload.successfulFiles
    const failedFiles = payload.failedFiles
    const processedFiles = payload.processedFiles
    const message = payload.message

    const listQueries = queryClient.getQueriesData({ queryKey: ['productPictureImports'] })

    listQueries.forEach(([queryKey, oldData]: [any, any]) => {
      if (!oldData || !oldData.items) {
        return
      }

      const itemExists = oldData.items.some((item: any) => item.id === importId)

      if (itemExists) {
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: oldData.items.map((item: any) =>
            item.id === importId
              ? {
                  ...item,
                  status,
                  successfulFiles,
                  failedFiles,
                  processedFiles,
                  message,
                  updatedAt: payload.updatedAt
                }
              : item
          )
        })
      }
    })

    const detailQueries = queryClient.getQueriesData({ queryKey: ['productPictureImport', importId] })

    detailQueries.forEach(([queryKey, oldData]: [any, any]) => {
      if (!oldData) {
        return
      }

      queryClient.setQueryData(queryKey, {
        ...oldData,
        status,
        successfulFiles,
        failedFiles,
        processedFiles,
        message,
        updatedAt: payload.updatedAt
      })
    })

    await queryClient.invalidateQueries({
      queryKey: ['productPictureImports'],
      refetchType: 'all'
    })

    await queryClient.invalidateQueries({
      queryKey: ['productPictureImport', importId],
      refetchType: 'all'
    })

    await queryClient.invalidateQueries({
      queryKey: ['products'],
      refetchType: 'all'
    })

    await queryClient.invalidateQueries({
      queryKey: ['product'],
      refetchType: 'all'
    })
  }
}
